import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { X, Search, Sparkles, Briefcase, CheckCircle2, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const LS_KEY = 'mm:onboarding-dismissed';

interface Step {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  href: string;
  linkLabel: string;
  done: boolean;
}

interface GetStartedBannerProps {
  hasAnalyses: boolean;
  hasPositions: boolean;
}

export function GetStartedBanner({ hasAnalyses, hasPositions }: GetStartedBannerProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Auto-dismiss once all three steps are complete
  const allDone = hasAnalyses && hasPositions;
  useEffect(() => {
    if (!allDone || dismissed) return;
    // Give a small delay so the user sees the completed state
    const t = setTimeout(() => {
      setDismissed(true);
      try { localStorage.setItem(LS_KEY, '1'); } catch {}
    }, 2000);
    return () => clearTimeout(t);
  }, [allDone, dismissed]);

  if (dismissed) return null;

  const steps: Step[] = [
    {
      id: 'browse',
      icon: Search,
      title: 'Browse markets',
      description: 'Explore prediction markets across Polymarket and Kalshi.',
      href: '/markets',
      linkLabel: 'Go to Markets →',
      // Browsing is considered done once they have an analysis (implies they visited markets)
      done: hasAnalyses,
    },
    {
      id: 'analyze',
      icon: Sparkles,
      title: 'Run your first AI analysis',
      description: 'Open any market and click "Analyze with Claude" to get an AI recommendation.',
      href: '/markets',
      linkLabel: 'Pick a market →',
      done: hasAnalyses,
    },
    {
      id: 'position',
      icon: Briefcase,
      title: 'Add your first position',
      description: 'Track a trade on any market page to see your P&L in the dashboard.',
      href: '/portfolio',
      linkLabel: 'View Portfolio →',
      done: hasPositions,
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const progressPct = Math.round((completedCount / steps.length) * 100);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(LS_KEY, '1'); } catch {}
  };

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 relative">
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss getting started guide"
        className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Header */}
      <div className="mb-4 pr-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-primary" />
          <h2 className="font-semibold text-sm">Get started with MarketMind</h2>
          <span className="ml-auto text-xs text-muted-foreground">
            {completedCount}/{steps.length} done
          </span>
        </div>
        {/* Progress bar */}
        <div className="h-1.5 rounded-full bg-primary/15 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            <div
              key={step.id}
              className={cn(
                'flex items-start gap-3 rounded-lg p-3 transition-colors',
                step.done
                  ? 'bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800'
                  : 'bg-background border border-border',
              )}
            >
              {/* Step number / check */}
              <div className="mt-0.5 shrink-0">
                {step.done ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground/40" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <Icon className={cn('h-3.5 w-3.5', step.done ? 'text-emerald-500' : 'text-primary')} />
                  <p className={cn('text-sm font-medium', step.done && 'line-through text-muted-foreground')}>
                    {idx + 1}. {step.title}
                  </p>
                </div>
                {!step.done && (
                  <p className="text-xs text-muted-foreground mb-2">{step.description}</p>
                )}
              </div>

              {/* Action link */}
              {!step.done && (
                <Link href={step.href} className="shrink-0 text-xs font-medium text-primary hover:underline whitespace-nowrap">
                  {step.linkLabel}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer dismiss */}
      <div className="mt-4 text-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="text-xs text-muted-foreground h-auto py-1"
        >
          I know what I'm doing — hide this
        </Button>
      </div>
    </div>
  );
}
